import type { CSSProperties, PropsWithChildren } from "react";
import primaryLogo from "../../ReqsAndAssets/1 - Brand Assets/Logos/Primary.png";
import secondaryLogo from "../../ReqsAndAssets/1 - Brand Assets/Logos/Secondary.png";
import cityArtwork from "../../ReqsAndAssets/1 - Brand Assets/Logos/Transparent City Only.png";
import advocateIcon from "../../ReqsAndAssets/PathwayIcons/advocate_icon.png";
import connectorIcon from "../../ReqsAndAssets/PathwayIcons/connector_icon.png";
import creatorIcon from "../../ReqsAndAssets/PathwayIcons/creator_icon.png";
import cultivatorIcon from "../../ReqsAndAssets/PathwayIcons/cultivator_icon.png";
import hostIcon from "../../ReqsAndAssets/PathwayIcons/host_icon.png";
import investorIcon from "../../ReqsAndAssets/PathwayIcons/investor_icon.png";
import pioneerIcon from "../../ReqsAndAssets/PathwayIcons/pioneer_icon.png";
import restorerIcon from "../../ReqsAndAssets/PathwayIcons/restorer_icon.png";
import shepherdIcon from "../../ReqsAndAssets/PathwayIcons/shepherd_icon.png";

const pathwayIcons: Record<string, string> = {
  Advocate: advocateIcon,
  Connector: connectorIcon,
  Creator: creatorIcon,
  Cultivator: cultivatorIcon,
  Host: hostIcon,
  Investor: investorIcon,
  Pioneer: pioneerIcon,
  Restorer: restorerIcon,
  Shepherd: shepherdIcon,
};

export function BrcLogo() {
  return (
    <div className="brc-logo">
      <img src={secondaryLogo} alt="BRC — Building Redemptive Community" />
      <svg
        className="brc-logo-overlay"
        viewBox="0 0 1350 1350"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <text x="1118" y="948" className="brc-registered-mark">®</text>
      </svg>
    </div>
  );
}

export function BrcPrimaryLogo() {
  return (
    <div className="brc-primary-logo">
      <img src={primaryLogo} alt="Building Redemptive Community" />
      <svg
        className="brc-primary-logo-overlay"
        viewBox="0 0 1350 1350"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <text x="1196" y="958" className="brc-primary-registered-mark">®</text>
      </svg>
    </div>
  );
}

export function BrandHeader({ saved }: { saved?: boolean }) {
  return (
    <header className="brand-header no-print">
      <a className="brand-link" href="/" aria-label="Redemptive Builder Inventory home">
        <div className="brand-logo-container">
          <BrcLogo />
        </div>
        <span>Redemptive Builder Inventory</span>
      </a>
      {saved && <span className="saved-indicator"><i /> Progress saved</span>}
    </header>
  );
}

export function PageShell({
  children,
  saved,
  className = "",
}: PropsWithChildren<{ saved?: boolean; className?: string }>) {
  return (
    <div className={`site-shell ${className}`}>
      <BrandHeader saved={saved} />
      <main>{children}</main>
      <footer className="site-footer no-print">
        <img src={cityArtwork} alt="" />
        <p>From inspiration to participation.</p>
      </footer>
    </div>
  );
}

export function Progress({ value, label }: { value: number; label: string }) {
  return (
    <div className="progress-wrap" aria-label={label}>
      <div className="progress-copy">
        <span>{label}</span>
        <span>{Math.round(value)}%</span>
      </div>
      <div className="progress-track">
        <span style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
      </div>
    </div>
  );
}

const journeySteps = [
  "Welcome",
  "Your info",
  "Inventory",
  "Your pathway",
  "Builder Journey",
  "Summary",
  "Complete",
] as const;

export function journeyProgressStep(screen: string) {
  if (screen === "welcome") return 0;
  if (screen === "info" || screen === "resume") return 1;
  if (screen === "inventory" || screen.startsWith("part-")) return 2;
  if (screen === "reveal") return 3;
  if (screen === "journey" || screen === "journey-intro") return 4;
  if (screen === "summary") return 5;
  return 6;
}

export function JourneyProgress({ screen }: { screen: string }) {
  const activeStep = journeyProgressStep(screen);
  const complete = activeStep === journeySteps.length - 1;
  const percentage = (activeStep / (journeySteps.length - 1)) * 100;
  return (
    <>
      <aside
        className={`journey-progress ${complete ? "is-complete" : ""}`}
        aria-label="Your progress through the Redemptive Builder Inventory"
      >
        <div className="journey-progress-heading">
          <span>{complete ? "Journey complete" : "Your journey"}</span>
          <strong aria-live="polite">{journeySteps[activeStep]}</strong>
        </div>
        <div className="journey-progress-track" aria-hidden="true"><i style={{ width: `${percentage}%` }} /></div>
        <ol>
          {journeySteps.map((label, index) => {
            const done = complete || index < activeStep;
            const current = !complete && index === activeStep;
            return (
              <li
                className={done ? "is-done" : current ? "is-current" : ""}
                key={label}
                aria-current={current ? "step" : undefined}
                style={{ "--step-delay": `${index * 80}ms` } as CSSProperties}
              >
                <span className="journey-progress-node" aria-hidden="true">{done ? "✓" : index + 1}</span>
                <span className="journey-progress-label">{label}</span>
              </li>
            );
          })}
        </ol>
      </aside>
      <div className="journey-progress-spacer" aria-hidden="true" />
    </>
  );
}

export function PathwayMark({ name, large = false }: { name: string; large?: boolean }) {
  const icon = pathwayIcons[name];
  return (
    <span className={`pathway-mark pathway-${name.toLowerCase()} ${large ? "large" : ""}`} aria-hidden="true">
      {icon ? <img src={icon} alt="" /> : name.slice(0, 1)}
    </span>
  );
}

export function ErrorNotice({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div className="error-notice" role="alert">
      <span aria-hidden="true">!</span>
      {message}
    </div>
  );
}

export function LoadingScreen({ message = "Preparing your journey..." }: { message?: string }) {
  return (
    <div className="loading-screen" aria-live="polite">
      <span className="loading-mark" />
      <p>{message}</p>
    </div>
  );
}
